/**
 * V2ChangeCard — the post-video "approve this change" gate. When the user asks
 * the agent to modify an ALREADY-generated video, the planner returns a change
 * request (summary + instruction); we show it here with the credit cost and
 * Approve / Reject. Approve → re-render the same HeyGen session with the change.
 * Reject → the conversation just continues. This is the SECOND (and only other)
 * gate to HeyGen besides the Generate button.
 */
import { Wand2, Loader2, Check, X, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/v2/utils';
import { MarkdownText } from '@/v2/components/MarkdownText';

export interface V2ChangeCardProps {
  summary: string;
  /** Our credit cost for re-rendering (calculateHeygenCredits of the video length). */
  credits: number;
  /** Live = show Approve/Reject; otherwise the card is a settled record. */
  actionable: boolean;
  busy?: boolean;
  resolved?: 'approved' | 'rejected' | null;
  /** Provider quotes itemize the cost; omitted = no breakdown, as today. */
  items?: { label: string; credits: number }[];
  /** Overrides the header credit pill's text. Unused now that both providers
   *  quote Vivideo credits; kept so a caller can still label a pill. */
  creditsLabel?: string;
  onApprove: () => void;
  onReject: () => void;
}

export function V2ChangeCard({ summary, credits, actionable, busy, resolved, items, creditsLabel, onApprove, onReject }: V2ChangeCardProps) {
  const { t } = useTranslation('v2');
  return (
    <div className="rounded-2xl border border-accent/40 bg-accent/5 overflow-hidden animate-slide-up">
      <div className="flex items-center gap-2.5 px-4 py-3 border-b border-glass-border bg-accent/10">
        <span className="w-7 h-7 rounded-lg bg-accent/20 text-accent flex items-center justify-center shrink-0">
          <Wand2 className="w-4 h-4" />
        </span>
        <span className="text-sm font-bold text-text-primary">{t('v2ChangeCard.editYourVideo')}</span>
        <span className="ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent/15 border border-accent/30 text-xs font-bold text-accent shrink-0">
          <Sparkles className="w-3.5 h-3.5" /> {creditsLabel ?? t('v2ChangeCard.credits', { count: credits })}
        </span>
      </div>

      <div className="p-4 space-y-3">
        <p className="text-sm text-text-secondary leading-relaxed"><MarkdownText text={summary} /></p>

        {/* Quote breakdown — only when the provider itemized it (LongVid quotes). */}
        {items && items.length > 0 && (
          <div>
            <p className="text-[11px] uppercase tracking-wide font-semibold text-text-tertiary mb-1.5">{t('v2ChangeCard.itemsTitle')}</p>
            <ul className="space-y-1">
              {/* Decimals come back as strings from DynamoDB — coerce before rendering. */}
              {items.map((it, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-text-secondary">
                  <span className="flex-1 leading-relaxed">{it.label}</span>
                  {Number.isFinite(Number(it.credits)) && (
                    <span className="shrink-0 tabular-nums text-text-tertiary">{Number(it.credits)}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {actionable && !resolved && (
          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={onApprove}
              disabled={busy}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-primary to-accent text-white text-sm font-bold hover:opacity-90 hover:shadow-glow transition-all disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {t('v2ChangeCard.approveAndRerender')}
            </button>
            <button
              onClick={onReject}
              disabled={busy}
              className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-surface-light border border-glass-border text-sm font-semibold text-text-secondary hover:text-text-primary transition-colors disabled:opacity-60"
            >
              <X className="w-4 h-4" /> {t('v2ChangeCard.discard')}
            </button>
          </div>
        )}

        {resolved && (
          <p className={cn('text-xs font-bold flex items-center gap-1.5', resolved === 'approved' ? 'text-emerald-400' : 'text-text-tertiary')}>
            {resolved === 'approved' ? <><Check className="w-3.5 h-3.5" /> {t('v2ChangeCard.changeApproved')}</> : <><X className="w-3.5 h-3.5" /> {t('v2ChangeCard.changeDiscarded')}</>}
          </p>
        )}
      </div>
    </div>
  );
}
